import { useQuery } from '@tanstack/react-query';
import { useApps } from '../../stores/appStore';
import { AppCard } from './AppCard';
import { AppGridSkeleton } from './AppGridSkeleton';

interface AppGridProps {
  searchQuery?: string;
  category?: string;
  limit?: number;
}

export function AppGrid({ searchQuery = '', category, limit }: AppGridProps) {
  const { apps, fetchApps } = useApps();

  const { data: filteredApps, isLoading, error } = useQuery({
    queryKey: ['apps', searchQuery, category],
    queryFn: async () => {
      await fetchApps();
      
      let filtered = apps;
      
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(app => 
          app.name.toLowerCase().includes(query) ||
          app.description?.toLowerCase().includes(query) ||
          app.tags?.some(tag => tag.toLowerCase().includes(query))
        );
      }
      
      if (category && category !== 'all') {
        filtered = filtered.filter(app => 
          app.category === category
        );
      }
      
      if (limit) {
        filtered = filtered.slice(0, limit);
      }
      
      return filtered;
    },
    enabled: true,
  });

  if (isLoading) {
    return <AppGridSkeleton />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 dark:text-red-400 mb-2">
          Failed to load apps
        </div>
        <button 
          onClick={() => fetchApps()}
          className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!filteredApps?.length) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500 dark:text-gray-400 mb-2">
          {searchQuery ? 'No apps found matching your search' : 'No apps available'}
        </div>
        {searchQuery && (
          <p className="text-sm text-gray-400 dark:text-gray-500">
            Try adjusting your search terms or browse by category
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
      {filteredApps.map((app) => (
        <AppCard key={app.id} app={app} />
      ))}
    </div>
  );
}